import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Mail, Check } from "lucide-react";

// V1 Decor Galvá · el usuario pega el correo (permiso/confirmación), la IA PROPONE cambios por línea existente,
// se muestra actual vs propuesto y sólo se aplica lo marcado tras confirmar. Nunca recrea la petición.
export default function ActualizarDesdeCorreoDialog({ peticionId, open, onClose, onApplied, initialRaw, initialAsunto }) {
  const [asunto, setAsunto] = useState("");
  const [raw, setRaw] = useState("");
  useEffect(() => { if (open && initialRaw) { setRaw(initialRaw); setAsunto(initialAsunto || ""); } }, [open, initialRaw, initialAsunto]);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => { setRes(null); setRaw(""); setAsunto(""); };

  const analizar = async () => {
    if (!raw.trim()) return toast.error("Pega el correo completo");
    setLoading(true);
    try {
      const { data } = await api.post(`/peticiones/${peticionId}/actualizar-desde-correo/analizar`, { email_raw: raw, email_subject: asunto || null });
      setRes(data);
      if (!data.propuestas?.length) toast.info("La IA no ha encontrado cambios aplicables a líneas existentes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error analizando el correo");
    } finally { setLoading(false); }
  };

  const toggle = (pi, ci) => setRes((r) => ({
    ...r, propuestas: r.propuestas.map((p, i) => i !== pi ? p : { ...p, cambios: p.cambios.map((c, j) => j !== ci ? c : { ...c, aplicar: !c.aplicar }) }),
  }));
  const editar = (pi, ci, v) => setRes((r) => ({
    ...r, propuestas: r.propuestas.map((p, i) => i !== pi ? p : { ...p, cambios: p.cambios.map((c, j) => j !== ci ? c : { ...c, propuesto: v }) }),
  }));

  const seleccionados = (res?.propuestas || []).flatMap((p) => p.cambios.filter((c) => c.aplicar).map((c) => ({ ...c, linea_id: p.linea_id, match_motivo: p.match_motivo })));

  const aplicar = async (forzar = false) => {
    if (!seleccionados.length) return toast.error("No hay cambios marcados");
    const yaAplicado = (res.ya_aplicado || []).length > 0;
    const msg = yaAplicado
      ? `⚠️ Este mismo correo YA se aplicó a esta petición. ¿Reaplicar ${seleccionados.length} cambio(s) de todas formas? Quedará marcado como reaplicado.`
      : `¿Aplicar ${seleccionados.length} cambio(s) a la petición? Quedará trazado el correo origen.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/peticiones/${peticionId}/actualizar-desde-correo/aplicar`, {
        cambios: seleccionados.map(({ aplicar: _a, ...c }) => c), correo: res.correo, forzar_reaplicar: yaAplicado || forzar,
      });
      toast.success(`${data.aplicados} cambio(s) aplicados`);
      onApplied?.(); reset(); onClose();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "correo_ya_aplicado") {
        setRes((r) => ({ ...r, ya_aplicado: [...(r.ya_aplicado || []), { at: "", por: "?", n_cambios: "?" }], avisos: [...(r.avisos || []), det.mensaje] }));
        toast.error(det.mensaje);
      } else toast.error(typeof det === "string" ? det : "Error aplicando cambios");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="actualizar-correo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-sky-400" /> Actualizar desde correo</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Pega el correo de permiso/confirmación. La IA propone cambios sobre las líneas ya existentes; tú revisas y confirmas. No se recrea nada.
          </DialogDescription>
        </DialogHeader>

        {!res ? (
          <div className="space-y-2">
            <Input value={asunto} onChange={(e) => setAsunto(e.target.value)} placeholder="Asunto (opcional, admite RE:/FW:)"
              className="bg-zinc-900 border-zinc-800" data-testid="correo-asunto" />
            <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={12} placeholder="Pega aquí el correo completo…"
              className="bg-zinc-900 border-zinc-800 text-xs font-mono" data-testid="correo-raw" />
          </div>
        ) : (
          <div className="space-y-3">
            {res.resumen && <div className="bg-violet-500/5 border border-violet-500/20 rounded p-2 text-xs text-violet-200">🤖 {res.resumen}</div>}
            <div className="text-[10px] text-zinc-500 flex items-center gap-2 flex-wrap">
              <span>Asunto normalizado: <b className="text-zinc-300">{res.asunto_normalizado || "—"}</b> · Fecha correo: {res.fecha_email || "—"}</span>
              {res.contexto_asunto?.ensenas?.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded border ${res.contexto_asunto.ambigua ? "bg-amber-500/10 border-amber-500/40 text-amber-200" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"}`}
                  data-testid="correo-ensena-asunto">
                  {res.contexto_asunto.ambigua ? `Asunto: varias enseñas (${res.contexto_asunto.ensenas.join(", ")})` : `Enseña del asunto: ${res.contexto_asunto.ensena}`}
                </span>
              )}
            </div>
            {(res.ya_aplicado || []).length > 0 && (
              <div className="bg-red-500/10 border border-red-500/50 rounded p-2 text-xs text-red-100" data-testid="correo-ya-aplicado">
                ⛔ <b>Este correo ya se aplicó a esta petición</b> ({res.ya_aplicado.length} vez/veces). Sólo se reaplicará si lo confirmas explícitamente al pulsar «Aplicar».
              </div>
            )}
            {(res.avisos || []).length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/40 rounded p-2 text-xs text-amber-100" data-testid="correo-avisos">
                {res.avisos.map((a, i) => <div key={i}>⚠ {a}</div>)}
              </div>
            )}
            {res.propuestas.map((p, pi) => (
              <div key={`${p.linea_id}-${pi}`} className={`border rounded-lg p-2 ${p.revisar ? "border-amber-500/50 bg-amber-500/5" : "border-zinc-800"}`} data-testid={`propuesta-${pi}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <div><b>{p.espacio_nombre}</b>{p.cadena_nombre && <span className="ml-1 text-[9px] uppercase tracking-wider px-1 rounded bg-zinc-800 text-zinc-400">{p.cadena_nombre}</span>} <span className="text-zinc-500">· {p.ciudad || ""} · línea {p.estado_linea}</span>{p.descripcion_linea && <span className="text-zinc-500"> · {p.descripcion_linea}</span>}</div>
                  <span className="text-[10px] text-zinc-500">match {Math.round((p.match_score || 0) * 100)}% · «{p.origen_texto}»</span>
                </div>
                {p.match_motivo && <div className="text-[10px] text-emerald-300/90 mb-1" data-testid={`propuesta-motivo-${pi}`}>✓ {p.match_motivo}</div>}
                {p.revisar && <div className="text-[10px] text-amber-200 mb-1" data-testid={`propuesta-revisar-${pi}`}>Requiere confirmación: los cambios de esta línea vienen desmarcados.</div>}
                {(p.avisos || []).map((a, k) => <div key={k} className="text-[10px] text-amber-300">⚠ {a}</div>)}
                {p.cambios.length === 0 && <div className="text-[10px] text-zinc-500 italic">Sin cambios respecto a lo actual.</div>}
                {p.cambios.length > 0 && (
                  <table className="w-full text-[11px] mt-1">
                    <thead><tr className="text-zinc-500 text-left"><th className="w-6"></th><th>Campo</th><th>Actual</th><th>Propuesto (editable)</th></tr></thead>
                    <tbody>
                      {p.cambios.map((c, ci) => (
                        <tr key={ci} className="border-t border-zinc-800/60" data-testid={`cambio-${pi}-${ci}`}>
                          <td><input type="checkbox" checked={!!c.aplicar} onChange={() => toggle(pi, ci)} data-testid={`cambio-check-${pi}-${ci}`} className="accent-emerald-500" /></td>
                          <td className="text-zinc-300 py-1">{c.label || c.campo}</td>
                          <td className="text-zinc-500 py-1">{c.actual || <i>— vacío —</i>}</td>
                          <td className="py-1"><Input value={c.propuesto ?? ""} onChange={(e) => editar(pi, ci, e.target.value)} className="bg-zinc-900 border-zinc-800 h-7 text-[11px]" data-testid={`cambio-propuesto-${pi}-${ci}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {p.cambios.some((c) => c.campo === "componente_grafica") && (
                  <div className="text-[10px] text-zinc-500 mt-1" data-testid={`propuesta-nota-grafica-${pi}`}>ℹ El cambio de gráfica se guarda como PONER/QUITAR en el componente (el nombre técnico, material y medidas no se tocan) y viaja al parte, Mi Ruta y Producción.</div>
                )}
              </div>
            ))}
            {(res.sin_match || []).length > 0 && (
              <div className="border border-dashed border-zinc-700 rounded p-2 text-xs text-zinc-400" data-testid="correo-sin-match">
                <div className="font-semibold text-zinc-300 mb-1">Menciones sin línea clara (no se aplican):</div>
                {res.sin_match.map((s, i) => (
                  <div key={i}>• «{s.espacio_nombre}» {s.fecha_texto && <>· {s.fecha_texto}</>} {s.candidatos?.length > 0 && <span className="text-zinc-500">→ ¿{s.candidatos.map((c) => `${c.espacio_nombre} ${Math.round(c.score * 100)}%${c.motivo ? ` (${c.motivo})` : ""}`).join(" / ")}?</span>}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          {res && <Button variant="ghost" onClick={() => setRes(null)} className="text-zinc-400">← Volver al correo</Button>}
          <Button variant="outline" onClick={() => { reset(); onClose(); }} className="bg-transparent border-zinc-700" disabled={saving}>Cancelar</Button>
          {!res ? (
            <Button onClick={analizar} disabled={loading || !raw.trim()} className="bg-sky-500 hover:bg-sky-400 text-zinc-950 font-semibold" data-testid="correo-analizar-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando…</> : "Analizar correo"}
            </Button>
          ) : (
            <Button onClick={() => aplicar(false)} disabled={saving || !seleccionados.length} className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold" data-testid="correo-aplicar-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Aplicar {seleccionados.length} cambio(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
